import { useState } from 'react';

type IntroStep = {
  title: string;
  lead: string;
  items?: Array<{ icon: string; title: string; body: string }>;
  steps?: Array<{ title: string; body: string }>;
};

const INTRO_STEPS: IntroStep[] = [
  {
    title: 'What is Crop Calendar?',
    lead:
      'A simple season guide for your field. We track your crop from sowing to harvest and explain where things stand — in plain words, not technical jargon.',
  },
  {
    title: 'What you will get',
    lead: 'After you load your field, your report includes:',
    items: [
      {
        icon: 'ri-plant-line',
        title: 'Where your crop is now',
        body: 'Current growth stage with a short explanation you can share with your team.',
      },
      {
        icon: 'ri-calendar-check-line',
        title: 'Harvest window',
        body: 'Your planned harvest date compared with a field estimate from satellite views.',
      },
      {
        icon: 'ri-lightbulb-line',
        title: 'What to do next',
        body: 'The next stage, rough timing, and one practical tip for this week.',
      },
      {
        icon: 'ri-line-chart-line',
        title: 'Greenness over the season',
        body: 'A simple chart showing how your field greened up since sowing or transplanting.',
      },
    ],
  },
  {
    title: 'How to start',
    lead: 'Three quick steps — usually ready in under a minute.',
    steps: [
      {
        title: 'Select your field',
        body: 'Pick a saved field on the map so we know which parcel to read.',
      },
      {
        title: 'Confirm crop details',
        body: 'Crop type, planting method, and sowing date — we can prefill from your saved record.',
      },
      {
        title: 'Load crop calendar',
        body: 'Tap the button and we fetch greenness, build your timeline, and draft your crop story.',
      },
    ],
  },
];

export default function CropCalendarIntro({
  isDark,
  onComplete,
  onTrySample,
}: {
  isDark: boolean;
  onComplete: () => void;
  onTrySample: () => void;
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = INTRO_STEPS[stepIndex];
  const isLast = stepIndex >= INTRO_STEPS.length - 1;

  const card = isDark
    ? 'rounded-2xl border border-[#2d3748] bg-[#1c2432] p-3.5'
    : 'rounded-2xl border border-[#e5e7eb] bg-white p-3.5';
  const label = `text-xs font-bold uppercase tracking-wide ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`;
  const muted = isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]';
  const heading = isDark ? 'text-[#e6edf3]' : 'text-[#111827]';
  const btn = `inline-flex min-h-[44px] items-center justify-center rounded-xl border px-3 text-sm font-semibold active:scale-[0.98] ${
    isDark ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3]' : 'border-[#e5e7eb] bg-white text-[#111827]'
  }`;
  const btnPri = `inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl px-3 text-sm font-semibold active:scale-[0.98] ${
    isDark ? 'bg-[#34d399] text-[#06210f]' : 'bg-[#008540] text-white'
  }`;
  const accent = isDark ? 'text-[#34d399]' : 'text-[#008540]';
  const itemBg = isDark ? 'bg-[#161b22] border-[#2d3748]' : 'bg-[#f3f4f6] border-[#e5e7eb]';

  return (
    <div className={card}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className={label}>Crop calendar</div>
        <div className={`text-xs font-semibold ${muted}`} aria-live="polite">
          Step {stepIndex + 1} of {INTRO_STEPS.length}
        </div>
      </div>

      <div className="mb-3 flex gap-1.5" aria-hidden>
        {INTRO_STEPS.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i <= stepIndex
                ? isDark
                  ? 'bg-[#34d399]'
                  : 'bg-[#008540]'
                : isDark
                  ? 'bg-[#2d3748]'
                  : 'bg-[#e5e7eb]'
            }`}
          />
        ))}
      </div>

      <h3 className={`text-lg font-bold leading-snug ${heading}`}>{step.title}</h3>
      <p className={`mt-2 text-sm leading-relaxed ${muted}`}>{step.lead}</p>

      {step.items && (
        <ul className="mt-3 space-y-2">
          {step.items.map((item) => (
            <li
              key={item.title}
              className={`flex gap-3 rounded-xl border p-3 ${itemBg}`}
            >
              <i className={`${item.icon} mt-0.5 shrink-0 text-lg ${accent}`} aria-hidden />
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${heading}`}>{item.title}</p>
                <p className={`mt-0.5 text-[13px] leading-snug ${muted}`}>{item.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {step.steps && (
        <ol className="mt-3 space-y-2">
          {step.steps.map((item, i) => (
            <li
              key={item.title}
              className={`flex gap-3 rounded-xl border p-3 ${itemBg}`}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  isDark ? 'bg-[#1a3324] text-[#34d399]' : 'bg-[#e3f2e8] text-[#008540]'
                }`}
              >
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${heading}`}>{item.title}</p>
                <p className={`mt-0.5 text-[13px] leading-snug ${muted}`}>{item.body}</p>
              </div>
            </li>
          ))}
        </ol>
      )}

      <div className="mt-4 flex gap-2">
        {stepIndex > 0 ? (
          <button
            type="button"
            className={`${btn} flex-1`}
            onClick={() => setStepIndex((i) => i - 1)}
          >
            Back
          </button>
        ) : (
          <button type="button" className={`${btn} flex-1`} onClick={onComplete}>
            Skip intro
          </button>
        )}
        {isLast ? (
          <button type="button" className={btnPri} onClick={onComplete}>
            Get started
          </button>
        ) : (
          <button
            type="button"
            className={btnPri}
            onClick={() => setStepIndex((i) => i + 1)}
          >
            Next
          </button>
        )}
      </div>

      {isLast && (
        <button
          type="button"
          onClick={onTrySample}
          className={`mt-2 w-full text-center text-[13px] font-semibold underline-offset-2 hover:underline ${accent}`}
        >
          Or try a sample first — no field needed
        </button>
      )}
    </div>
  );
}
